import { issueFairnessTicket, type ApiClient } from '../api';
import { bytesToHex, type PublicTicket } from '@game-platform/fairness';

/** A ticket waiting for the next purchase of its pack, with the player's seed for it. */
export interface HeldTicket {
  ticket: PublicTicket;
  /** 32 random bytes from this browser: the player's half of the roll. */
  clientSeed: string;
  /** Local clock, ms: when the server stops accepting the ticket. */
  expiresAt: number;
}

/** A held ticket is replaced this long before it expires, so a purchase never races the deadline. */
export const REFRESH_MARGIN_MS = 15_000;

const held = new Map<string, HeldTicket>();
const pending = new Map<string, Promise<HeldTicket | null>>();
const listeners = new Set<() => void>();
// Bumped by clearTickets (another session): late responses of the old one are dropped.
let generation = 0;

function notify(): void {
  for (const cb of listeners) cb();
}

export function newClientSeed(): string {
  const bytes = new Uint8Array(32);
  globalThis.crypto.getRandomValues(bytes);
  return bytesToHex(bytes);
}

function usable(ticket: HeldTicket | undefined, now: number): ticket is HeldTicket {
  return !!ticket && ticket.expiresAt - REFRESH_MARGIN_MS > now;
}

/** The ticket currently held for a pack (possibly near expiry) — for display. */
export function heldTicket(offerId: string): HeldTicket | null {
  return held.get(offerId) ?? null;
}

/**
 * A ticket fresh enough to buy with, issuing one when needed. Concurrent
 * callers share one request. Null when none could be issued (provably fair
 * off for this player, rate limit, network) — the purchase then goes ahead
 * without one and the server decides.
 */
export function ensureTicket(client: ApiClient, offerId: string): Promise<HeldTicket | null> {
  const current = held.get(offerId);
  if (usable(current, Date.now())) return Promise.resolve(current);
  const inflight = pending.get(offerId);
  if (inflight) return inflight;

  const gen = generation;
  // Measured before the request: the local deadline errs on the early side.
  const startedAt = Date.now();
  const request: Promise<HeldTicket | null> = issueFairnessTicket(client, offerId)
    .then((ticket) => {
      if (gen !== generation) return null;
      const lifetime = Math.max(0, ticket.valid_until_ms - ticket.issued_at_ms);
      const next: HeldTicket = { ticket, clientSeed: newClientSeed(), expiresAt: startedAt + lifetime };
      held.set(offerId, next);
      notify();
      return next;
    })
    .catch(() => null)
    .finally(() => {
      if (pending.get(offerId) === request) pending.delete(offerId);
    });
  pending.set(offerId, request);
  return request;
}

/** Hands a ticket to a purchase. A ticket buys once, so it leaves the store. */
export async function takeTicket(client: ApiClient, offerId: string): Promise<HeldTicket | null> {
  const ticket = await ensureTicket(client, offerId);
  if (ticket && held.get(offerId) === ticket) {
    held.delete(offerId);
    notify();
  }
  return ticket;
}

/** Drops every held ticket — they belong to the session that requested them. */
export function clearTickets(): void {
  generation += 1;
  held.clear();
  pending.clear();
  notify();
}

export function subscribeTickets(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}
